import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

export interface WarningModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  type?: 'warning' | 'error' | 'info' | 'success' | 'delete';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  loading?: boolean;
  confirmDisabled?: boolean;
  customIcon?: React.ReactNode;
  customContent?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  heading?: string;
  subHeadings?: string[];
}

const WarningModal: React.FC<WarningModalProps> = ({
  open,
  onClose,
  title,
  message,
  type = 'warning',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancelButton = true,
  loading = false,
  confirmDisabled = false,
  customIcon,
  customContent,
  maxWidth = 'sm',
  fullWidth = false,
  heading,
  subHeadings
}) => {
  const theme = useTheme();

  const getIcon = () => {
    if (customIcon) return customIcon;

    switch (type) {
      case 'error':
        return <ErrorIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />;
      case 'success':
        return <SuccessIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />;
      case 'delete':
        return <DeleteIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />;
      case 'warning':
      default:
        return <WarningIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />;
    }
  };

  const getTitleColor = () => {
    switch (type) {
      case 'error':
      case 'delete':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'info':
        return theme.palette.info.main;
      case 'warning':
      default:
        return theme.palette.warning.main;
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    // Don't automatically close - let the parent component control closing
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      // Only auto-close if no custom cancel handler
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          minWidth: 400,
          maxWidth: 500
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getIcon()}
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              color: getTitleColor(),
              fontSize: '1rem'
            }}
          >
            {title || 'Warning'}
          </Typography>
        </Box>
        <IconButton
          onClick={loading ? undefined : onClose}
          size="small"
          disabled={loading}
          sx={{
            color: loading ? theme.palette.grey[300] : theme.palette.grey[500],
            '&:hover': {
              color: loading ? theme.palette.grey[300] : theme.palette.grey[700],
              backgroundColor: loading ? 'transparent' : theme.palette.grey[100]
            },
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ py: 6, px: 3 }}>
        {customContent ? (
          customContent
        ) : (
          <Box sx={{ textAlign: 'left', py: 2 }}>
            {heading && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 2,
                  textAlign: 'left'
                }}
              >
                {heading}
              </Typography>
            )}

            {message && (
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  color: theme.palette.text.primary,
                  mb: subHeadings && subHeadings.length > 0 ? 2 : 0,
                  textAlign: 'left'
                }}
              >
                {message}
              </Typography>
            )}

            {subHeadings && subHeadings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {subHeadings.map((subHeading, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 1.5,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                        color: theme.palette.text.secondary,
                        textAlign: 'left'
                      }}
                    >
                      {subHeading}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
       
          backgroundColor: theme.palette.grey[50],
          justifyContent: 'flex-end'
        }}
      >
        {showCancelButton && (
          <Button
            onClick={handleCancel}
            variant="outlined"
            size="medium"
            disabled={loading}
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: theme.palette.grey[300],
              color: theme.palette.grey[700],
              '&:hover': {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.grey[50]
              }
            }}
          >
            {cancelText}
          </Button>
        )}

        <Button
          onClick={handleConfirm}
          variant="contained"
          size="medium"
          disabled={loading || confirmDisabled}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            minWidth: 100,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            backgroundColor: getTitleColor(),
            '&:hover': {
              backgroundColor: type === 'error' || type === 'delete'
                ? theme.palette.error.dark
                : type === 'success'
                ? theme.palette.success.dark
                : type === 'info'
                ? theme.palette.info.dark
                : theme.palette.warning.dark
            }
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarningModal;
